import React, { useEffect, useRef, useState } from "react";
import { theme, card, floatingSheet, btnPrimary, inputStyle, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";

import { cbPalette, cbSafe } from "../lib/colourBlind";
interface Department {
  id: string;
  name: string;
  description: string | null;
  color: string;
  isDefault: boolean;
  createdAt: string;
  memberCount?: number;
  /** Everything filed to the department, not controls alone — see docs/CROSS-REFERENCING.md. */
  taggedCount?: number;
  /** @deprecated the same number under its old, controls-only name. */
  controlCount?: number;
}

interface Tag {
  id: string;
  name: string;
  color: string;
  memberCount: number;
}

const PALETTE = cbPalette([
  "#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#6366f1",
  "#ec4899", "#ef4444", "#0ea5e9", "#84cc16", "#f97316",
  "#14b8a6", "#a855f7", "#64748b", "#d946ef", "#22c55e",
]);

// ── Colour picker ─────────────────────────────────────────────────────────────

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          style={{
            width: 22, height: 22, borderRadius: "50%", border: "none",
            background: c, cursor: "pointer",
            outline: value === c ? `2px solid ${theme.color.text}` : "2px solid transparent",
            outlineOffset: 2,
          }}
        />
      ))}
    </div>
  );
}

// ── Department chip ────────────────────────────────────────────────────────────

export function DeptChip({ name, color: stored }: { name: string; color: string }) {
  // A department colour is chosen by the organisation and stored, so it follows the theme here.
  const color = cbSafe(stored);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 600,
      background: color + "22", color, border: `1px solid ${color}55`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
      {name}
    </span>
  );
}

// ── Create / edit modal ───────────────────────────────────────────────────────

function DeptModal({
  initial, onClose, onSaved,
}: {
  initial?: Department;
  onClose: () => void;
  onSaved: (d: Department) => void;
}) {
  const [name,        setName]        = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [color,       setColor]       = useState(initial?.color ?? "#6366f1");
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const body = { name: name.trim(), description: description.trim() || (initial ? null : undefined), color };
      const result = initial
        ? await api.patch<{ department: Department }>(`/departments/${initial.id}`, body)
        : await api.post<{ department: Department }>("/departments", body);
      onSaved(result.department);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  const overlay: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  };
  const field: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
  const label: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, fontWeight: 600 };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...floatingSheet, padding: 20, width: "100%", maxWidth: 440 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>
            {initial ? "Edit department" : "New department"}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={field}>
            <label style={label}>Name</label>
            <input
              style={inputStyle} value={name} autoFocus required maxLength={60}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Data Engineering"
            />
          </div>
          <div style={field}>
            <label style={label}>Description (optional)</label>
            <input
              style={inputStyle} value={description} maxLength={200}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description"
            />
          </div>
          <div style={field}>
            <label style={label}>Colour</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ColorPicker value={color} onChange={setColor} />
              <DeptChip name={name || "Preview"} color={color} />
            </div>
          </div>
          {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ ...inputStyle, width: "auto", padding: "8px 16px", cursor: "pointer" }}>Cancel</button>
            <button type="submit" disabled={saving || !name.trim()} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Tags — informational labels for team members ──────────────────────────────

function TagsSection({ isAdmin }: { isAdmin: boolean }) {
  const [tags,     setTags]     = useState<Tag[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [name,     setName]     = useState("");
  const [color,    setColor]    = useState("#64748b");
  const [editing,  setEditing]  = useState<Tag | null>(null);
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState("");

  function load() {
    setLoading(true);
    api.get<{ tags: Tag[] }>("/user-tags")
      .then((d) => setTags(d.tags))
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  function fail(err: unknown, fallback: string) {
    setError(apiErrorMessage(err, fallback));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (editing) {
        const r = await api.patch<{ tag: Tag }>(`/user-tags/${editing.id}`, { name: name.trim(), color });
        setTags((prev) => prev.map((t) => (t.id === r.tag.id ? r.tag : t)).sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        const r = await api.post<{ tag: Tag }>("/user-tags", { name: name.trim(), color });
        setTags((prev) => [...prev, r.tag].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setName(""); setColor("#64748b"); setEditing(null);
    } catch (err) {
      fail(err, "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(t: Tag) {
    if (t.memberCount > 0 && !window.confirm(`Remove "${t.name}" from ${t.memberCount} member${t.memberCount === 1 ? "" : "s"} and delete it?`)) return;
    setBusy(true); setError("");
    try {
      await api.del(`/user-tags/${t.id}`);
      setTags((prev) => prev.filter((x) => x.id !== t.id));
      if (editing?.id === t.id) { setEditing(null); setName(""); setColor("#64748b"); }
    } catch (err) {
      fail(err, "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Member tags</div>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: theme.color.subtle, maxWidth: 560 }}>
        Labels for people — site, function, "DPO", "Incident responder". Tags help you find and group
        members on the Team page; they never change what someone can see or do.
      </p>

      <div style={{ ...card, padding: 16 }}>
        {loading ? (
          <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading…</div>
        ) : tags.length === 0 ? (
          <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: isAdmin ? 12 : 0 }}>No tags yet.</div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: isAdmin ? 16 : 0 }}>
            {tags.map((t) => (
              <div key={t.id} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <DeptChip name={`${t.name}${t.memberCount ? ` · ${t.memberCount}` : ""}`} color={t.color} />
                {isAdmin && (
                  <>
                    <button type="button" onClick={() => { setEditing(t); setName(t.name); setColor(t.color); setError(""); }}
                      style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 11, padding: 0 }}>edit</button>
                    <button type="button" onClick={() => handleDelete(t)} disabled={busy}
                      style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.danger, fontSize: 11, padding: 0 }}>delete</button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {isAdmin && (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", borderTop: tags.length ? `1px solid ${theme.color.border}` : "none", paddingTop: tags.length ? 14 : 0 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 180px" }}>
              <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>{editing ? `Edit tag — ${editing.name}` : "New tag"}</label>
              <input style={inputStyle} value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="e.g. Incident responder" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>Colour</label>
              <ColorPicker value={color} onChange={setColor} />
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button type="submit" disabled={busy || !name.trim()} style={{ ...btnPrimary, opacity: busy ? 0.7 : 1 }}>
                {busy ? "Saving…" : editing ? "Save" : "Add tag"}
              </button>
              {editing && (
                <button type="button" onClick={() => { setEditing(null); setName(""); setColor("#64748b"); }} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>Cancel</button>
              )}
            </div>
            {error && <div style={{ fontSize: 12, color: theme.color.dangerText, flexBasis: "100%" }}>{error}</div>}
          </form>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DepartmentsPage() {
  const { user } = useCurrentUser();
  // Mirrors the server: every write on this page needs org:admin, whoever the caller is.
  const isAdmin = !!user?.permissions?.includes("org:admin");

  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [showCreate,  setShowCreate]  = useState(false);
  const [editing,     setEditing]     = useState<Department | null>(null);
  const [confirmDel,  setConfirmDel]  = useState<string | null>(null);
  const [deleting,    setDeleting]    = useState(false);
  const [delError,    setDelError]    = useState("");
  const confirmRef = useRef<string | null>(null);
  confirmRef.current = confirmDel;

  function load() {
    setLoading(true);
    api.get<{ departments: Department[] }>("/departments")
      .then((d) => setDepartments(d.departments))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  function onSaved(dept: Department) {
    setDepartments((prev) => {
      const idx = prev.findIndex((d) => d.id === dept.id);
      // PATCH returns the bare row; keep the counts GET supplied.
      if (idx >= 0) { const next = [...prev]; next[idx] = { ...prev[idx], ...dept }; return next; }
      return [...prev, dept].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0) || a.name.localeCompare(b.name));
    });
  }

  async function handleDelete(id: string) {
    setDeleting(true); setDelError("");
    try {
      await api.del(`/departments/${id}`);
      setDepartments((prev) => prev.filter((d) => d.id !== id));
      setConfirmDel(null);
    } catch (err) {
      setDelError(apiErrorMessage(err, "Delete failed"));
    } finally {
      setDeleting(false);
    }
  }

  const defaults  = departments.filter((d) => d.isDefault);
  const custom    = departments.filter((d) => !d.isDefault);

  const cell: React.CSSProperties = {
    padding: "12px 16px", borderBottom: `1px solid ${theme.color.border}`,
    fontSize: 13, color: theme.color.text,
  };

  const th: React.CSSProperties = { ...cell, ...text.eyebrow, color: theme.color.muted, borderBottom: "1px solid var(--c-line)" };

  function renderRow(d: Department) {
    return (
      <React.Fragment key={d.id}>
        <tr>
          <td style={cell}>
            <DeptChip name={d.name} color={d.color} />
          </td>
          <td style={{ ...cell, color: theme.color.muted, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {d.description ?? "—"}
          </td>
          <td style={{ ...cell, width: 80 }}>
            {d.isDefault
              ? <span style={{ fontSize: 11, color: theme.color.subtle }}>Standard</span>
              : <span style={{ fontSize: 11, color: theme.color.accentText, background: theme.color.accentBg, padding: "2px 6px", borderRadius: 4 }}>Custom</span>}
          </td>
          <td style={{ ...cell, width: 120, fontSize: 11, color: theme.color.muted, whiteSpace: "nowrap" }}>
            {d.memberCount ?? 0} member{(d.memberCount ?? 0) === 1 ? "" : "s"}<br />
            <span title="Controls, risks, suppliers, assets and anything else filed to this department">
              {d.taggedCount ?? d.controlCount ?? 0} tagged
            </span>
          </td>
          {isAdmin && (
            <td style={{ ...cell, textAlign: "right", width: 160 }}>
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <button
                  onClick={() => setEditing(d)}
                  style={{ padding: "3px 10px", borderRadius: 4, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.text, fontSize: 12, cursor: "pointer" }}
                >
                  Edit
                </button>
                {!d.isDefault && (
                  <button
                    onClick={() => { setConfirmDel(d.id); setDelError(""); }}
                    style={{ padding: "3px 10px", borderRadius: 4, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.danger, fontSize: 12, cursor: "pointer" }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </td>
          )}
        </tr>
        {confirmDel === d.id && (
          <tr key={`${d.id}-confirm`}>
            <td colSpan={isAdmin ? 5 : 4} style={{ ...cell, background: theme.color.panelAlt }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13 }}>Delete <strong>{d.name}</strong>? Control tags and member assignments will be removed.</span>
                <button
                  onClick={() => handleDelete(d.id)} disabled={deleting}
                  style={{ padding: "3px 12px", borderRadius: 4, border: "none", background: theme.color.danger, color: "#fff", fontSize: 12, fontWeight: 600, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.6 : 1 }}
                >
                  {deleting ? "Deleting…" : "Yes, delete"}
                </button>
                <button
                  onClick={() => { setConfirmDel(null); setDelError(""); }}
                  style={{ padding: "3px 10px", borderRadius: 4, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.muted, fontSize: 12, cursor: "pointer" }}
                >
                  Cancel
                </button>
                {delError && <span style={{ fontSize: 12, color: theme.color.danger }}>{delError}</span>}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }

  // Plain function, not a component: a component declared inside render would
  // remount its subtree on every state change.
  function renderSection(title: string, rows: Department[]) {
    if (rows.length === 0) return null;
    return (
      <React.Fragment key={title}>
        <tr>
          <td colSpan={isAdmin ? 5 : 4} style={{ ...cell, background: theme.color.panelAlt, fontWeight: 600, fontSize: 11, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {title}
          </td>
        </tr>
        {rows.map(renderRow)}
      </React.Fragment>
    );
  }

  return (
    <div className="page-pad" style={{ fontFamily: theme.font }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Departments</h1>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
            Tag controls with departments to filter your SOA by team ownership. Members can belong to several departments.
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowCreate(true)} style={{ ...btnPrimary, flexShrink: 0 }}>
            + New department
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: theme.color.panelAlt }}>
                <th style={th}>Department</th>
                <th style={th}>Description</th>
                <th style={th}>Type</th>
                <th style={th}>In use</th>
                {isAdmin && <th style={{ ...th, width: 160 }} />}
              </tr>
            </thead>
            <tbody>
              {renderSection("Standard departments", defaults)}
              {renderSection("Custom departments", custom)}
              {departments.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 5 : 4} style={{ ...cell, textAlign: "center", color: theme.color.subtle, padding: "32px 16px" }}>
                    No departments yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <TagsSection isAdmin={!!isAdmin} />

      {showCreate && (
        <DeptModal onClose={() => setShowCreate(false)} onSaved={onSaved} />
      )}
      {editing && (
        <DeptModal initial={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </div>
  );
}
